import { useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { DashboardLayout } from "../components/DashboardLayout";
import { formatApiError } from "../lib/formatApiError";
import { API } from "../lib/constants";

export default function ImageGenerator() {
  const { user, refreshUser } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("modern");
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGenerate = async () => {
    if (!prompt.trim()) { setError("Please enter a prompt"); return; }
    setError("");
    setLoading(true);
    setImageData(null);
    try {
      const { data } = await axios.post(`${API}/content/generate-image`, { prompt, style });
      setImageData(data.image_data);
      refreshUser();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Image Generator</h1>
          <p className="text-gray-600">Create stunning visuals with Nano Banana AI</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white border border-black p-6">
            <h2 className="text-lg font-bold mb-4">Describe Your Image</h2>
            {error && <div className="bg-red-50 border border-red-500 text-red-700 p-3 mb-4 text-sm" data-testid="image-error">{error}</div>}
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Prompt</label>
                <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g., A professional team meeting in a modern office with natural lighting" rows={4} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none resize-none" data-testid="image-prompt" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Style</label>
                <select value={style} onChange={(e) => setStyle(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none" data-testid="style-select">
                  <option value="modern">Modern & Clean</option>
                  <option value="minimalist">Minimalist</option>
                  <option value="vibrant">Vibrant & Colorful</option>
                  <option value="professional">Professional</option>
                  <option value="artistic">Artistic</option>
                </select>
              </div>
              <div className="flex items-center justify-between pt-4">
                <span className="text-sm text-gray-500">Cost: 2 credits | Available: {user?.credits || 0}</span>
                <button onClick={handleGenerate} disabled={loading || (user?.credits || 0) < 2} className="bg-[#0066FF] text-white px-6 py-3 font-bold hover:bg-[#0052CC] transition-colors disabled:opacity-50" data-testid="generate-image-btn">
                  {loading ? "Generating..." : "Generate Image"}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white border border-black p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Generated Image</h2>
              {imageData && (
                <a href={`data:image/png;base64,${imageData}`} download="generated-image.png" className="text-sm text-[#0066FF] font-bold hover:underline" data-testid="download-image-btn">Download</a>
              )}
            </div>
            <div className="min-h-[400px] border border-gray-200 bg-gray-50 flex items-center justify-center" data-testid="image-result">
              {loading ? (
                <div className="animate-pulse text-gray-400">Generating your image...</div>
              ) : imageData ? (
                <img src={`data:image/png;base64,${imageData}`} alt="Generated" className="max-w-full max-h-[400px] object-contain" />
              ) : (
                <div className="text-gray-400">Your generated image will appear here</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
